import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tutorial.css';
import { tutorialSteps, tutorialStorageKeys } from './tutorialSteps';

const TutorialContext = createContext(null);

function existingStepsOnly(steps) {
  return steps.filter(step => {
    if (!step.element) return true;
    return !!document.querySelector(step.element);
  });
}

function mobileFriendlySteps(steps) {
  if (typeof window === 'undefined') return steps;
  const isMobile = window.matchMedia('(max-width: 1023px)').matches;
  if (!isMobile) return steps;

  return steps.map(step => ({
    ...step,
    popover: {
      ...step.popover,
      side: 'bottom',
      align: 'center'
    }
  }));
}

export function TutorialProvider({ children }) {
  const driverRef = useRef(null);

  const stopTutorial = useCallback(() => {
    driverRef.current?.destroy();
    driverRef.current = null;
  }, []);

  const startTutorial = useCallback((barbershopId, options = {}) => {
    if (!barbershopId) return;

    stopTutorial();
    const steps = existingStepsOnly(mobileFriendlySteps(options.steps || tutorialSteps));
    const instance = driver({
      showProgress: true,
      allowClose: true,
      animate: true,
      smoothScroll: true,
      stagePadding: 8,
      stageRadius: 18,
      overlayColor: 'rgba(3, 3, 10, 0.78)',
      nextBtnText: 'Proximo',
      prevBtnText: 'Voltar',
      doneBtnText: 'Finalizar',
      popoverClass: 'barberflow-driver-popover',
      onHighlightStarted: (element, step, hookOptions) => {
        options.onStepChange?.(step);
        window.setTimeout(() => {
          hookOptions?.driver?.refresh?.();
        }, 340);
      },
      onDestroyed: () => {
        if (options.markSeen !== false) {
          localStorage.setItem(tutorialStorageKeys.seen(barbershopId), 'true');
        }
        options.onFinish?.();
        driverRef.current = null;
      }
    });

    driverRef.current = instance;
    instance.setSteps(steps);
    instance.drive();
  }, [stopTutorial]);

  const restartTutorial = useCallback((barbershopId, options = {}) => {
    if (!barbershopId) return;
    localStorage.removeItem(tutorialStorageKeys.seen(barbershopId));
    startTutorial(barbershopId, { ...options, markSeen: true });
  }, [startTutorial]);

  const maybeStartTutorial = useCallback((barbershopId, options = {}) => {
    if (!barbershopId) return;
    const alreadySeen = localStorage.getItem(tutorialStorageKeys.seen(barbershopId)) === 'true';
    if (alreadySeen) return;
    window.setTimeout(() => startTutorial(barbershopId, options), 850);
  }, [startTutorial]);

  const value = useMemo(() => ({
    startTutorial,
    restartTutorial,
    maybeStartTutorial,
    stopTutorial
  }), [maybeStartTutorial, restartTutorial, startTutorial, stopTutorial]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const context = useContext(TutorialContext);
  if (!context) {
    return {
      startTutorial: () => {},
      restartTutorial: () => {},
      maybeStartTutorial: () => {},
      stopTutorial: () => {}
    };
  }
  return context;
}
